import { createContext, useContext, useEffect, useState } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

const useCreatePnlContext = () => {
  const [value_date, setValueDate] = useState<string>(toISO8601(getTMinus1()))
  const [fund_name, setFundName] = useState<string | null>(null)
  const [inspectingRow, setInspectingRow] = useState<PnlAttributions.AggregatedAttributionRow | null>(null)
  const [inspectingTrades, setInspectingTrades] = useState<PnlAttributions.AttributionSummaryRow | null>(null)
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null)

  const api = useApi()

  const isInspectingSummary = !!inspectingRow
  const isInspectingTrades = !!inspectingTrades

  const params: PnlAttributions.GetAttributionsStateParams = {
    value_date,
    fund_name: fund_name!,
  }

  const stateQuery = useQuery({
    queryKey: ['pnl-attributions', 'state', params],
    queryFn: () => api.pnlAttributions.getState(params),
    enabled: !!params.fund_name && !!params.value_date,
  })

  const selectedBatchState = selectedBatchId
    ? (stateQuery.data ?? []).find((s) => s.batch_id === selectedBatchId)
    : null

  useEffect(() => {
    if (!selectedBatchState && stateQuery.data) {
      setSelectedBatchId(stateQuery.data[0].batch_id)
    }
  }, [stateQuery.data])

  useEffect(() => {
    setInspectingRow(null)
    setInspectingTrades(null)
  }, [selectedBatchId])

  const inspectingBatchId = selectedBatchState?.batch_id
  const inspectingActionedByUser = selectedBatchState?.actioned_by_id

  const historyQuery = useQuery({
    queryKey: ['pnl-attributions', 'request-history', params],
    queryFn: () => api.pnlAttributions.getRequestHistory(params),
    enabled: !!params.fund_name && !!params.value_date,
  })

  const attributionsGroupedQuery = useQuery({
    queryKey: ['pnl-attributions', 'attributions-grouped', inspectingRow?.attribution_ids],
    queryFn: () => {
      return api.pnlAttributions.getAttributionsGrouped({
        attribution_ids: inspectingRow?.attribution_ids!,
      })
    },
    enabled: isInspectingSummary,
  })

  const attributionsQuery = useQuery({
    queryKey: ['pnl-attributions', 'attributions', inspectingRow?.attribution_ids],
    queryFn: () => {
      return api.pnlAttributions.getAttributions({
        attribution_ids: inspectingTrades?.attribution_ids!,
      })
    },
    enabled: isInspectingTrades,
  })

  return {
    params,
    selectedBatchId,
    setSelectedBatchId,
    setValueDate,
    setFundName,
    stateQuery,
    selectedBatchState,
    attributionsGroupedQuery,
    attributionsQuery,
    inspectingBatchId,
    inspectingActionedByUser,
    inspectingRow,
    setInspectingRow,
    inspectingTrades,
    setInspectingTrades,
    isInspectingSummary,
    isInspectingTrades,
    historyQuery,
  }
}

const pnlContext = createContext<ReturnType<typeof useCreatePnlContext> | null>(null)

export const usePnlContext = () => {
  const context = useContext(pnlContext)
  if (!context) throw new Error('Pnl context must be used within a <pnlContext.Provider/>')
  return context
}

export interface PnlContextProviderProps extends React.PropsWithChildren {}

export const PnlContextProvider: React.FC<PnlContextProviderProps> = ({ children }) => {
  const context = useCreatePnlContext()
  return <pnlContext.Provider value={context}>{children}</pnlContext.Provider>
}
